/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-12 11:34:36
 */

import { useCallback, useRef, useEffect } from 'react'
import { App } from 'antd'
import type {
  XCRecognitionResult,
  XCSafetyScenario,
  XCSelectionInfo
} from '../types'
import { XC_RECOGNITION_STATUS } from '../types'
import { useAIRecognition } from '../context/AIRecognitionContext'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { useNavigate } from 'react-router-dom'
import { fetchAIRecognition } from '../../../actions/aiRecognition'
import { useSensorTracking } from './useSensorTracking'
import { log } from 'console'

/**
 * 图片上传相关的hook
 */
export const useSelectedImage = () => {
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { reportAISafetyHazardsResultClick } = useSensorTracking()
  const {
    selectedImages,
    setSelectedImages,
    setRecognitionResult,
    setRecognitionStatus,
    userId,
    groupId
  } = useAIRecognition()

  // 从Redux store获取modelName
  const buildingSafetyAIModelName = useAppSelector(
    (state) => state.main.abTestConfig?.buildingSafetyAIModelName
  )

  /**
   * 添加图片
   */
  const addImage = useCallback(
    (images: string[]) => {
      setSelectedImages(images)
      message.success('图片添加成功')
    },
    [setSelectedImages]
  )

  /**
   * 添加多张图片
   */
  const addImages = useCallback(
    (images: string[]) => {
      setSelectedImages(images)
      message.success(`成功添加${images.length}张图片`)
    },
    [setSelectedImages]
  )

  /**
   * 调用识别接口
   */
  const handleRecognition = useCallback(
    async (
      image: string[],
      userID: string,
      selectionInfo: XCSelectionInfo[],
      scenariosToUse: XCSafetyScenario[]
    ) => {
      if (!userId || !groupId) {
        message.error('用户或团队信息缺失')
        return
      }

      setRecognitionStatus(XC_RECOGNITION_STATUS.RECOGNIZING)
      // 将selectedSafetyScenarios转换为API要求的格式
      const formattedUserChoice = scenariosToUse.map((scenario) => {
        // 检查是否是自定义选项
        const isCustom = scenario.id === '__custom__'
        const customContent =
          (scenario as any).customContent || scenario.description || ''

        return {
          scene: {
            key: isCustom ? '自定义' : scenario.name,
            value: isCustom
              ? [customContent]
              : scenario.description
              ? [scenario.description]
              : []
          },
          selected: 1
        }
      })

      const objs =
        selectionInfo.map((info) => [
          info.x,
          info.y,
          info.width,
          info.height
        ]) ?? [] // 将选区信息转换为 Array<Array<number>> 格式
      try {
        // 上报识别开始埋点
        reportAISafetyHazardsResultClick({
          clickItem: '',
          actionType: 'indentifyBegin',
          infoJson: JSON.stringify(objs),
          infoJson2: JSON.stringify(formattedUserChoice)
        })

        // 调用actions中的AI识别函数
        const result = await dispatch(
          fetchAIRecognition({
            groupID: groupId.toString(),
            url: image[image.length - 1],
            userID: userID,
            modelName: buildingSafetyAIModelName ?? '3B+10B_v0.3',
            user_choice: formattedUserChoice,
            objs: objs
          })
        )

        const response = result.data as XCRecognitionResult

        setRecognitionResult(response)
        setRecognitionStatus(XC_RECOGNITION_STATUS.COMPLETED)
        // 上报识别成功埋点
        reportAISafetyHazardsResultClick({
          clickItem: '',
          actionType: result.code === 200 ? 'indentifySucc' : 'indentifyFail',
          errorMsg: result.code === 200 ? undefined : result.msg,
          infoJson: JSON.stringify(objs),
          infoJson2: JSON.stringify(formattedUserChoice)
        })
        result.code !== 200 && message.error(result.msg)
      } catch (err) {
        // 上报识别失败埋点
        reportAISafetyHazardsResultClick({
          clickItem: '',
          actionType: 'indentifyFail',
          errorMsg: err instanceof Error ? err.message : '识别失败',
          taskID: '',
          infoJson: JSON.stringify(objs),
          infoJson2: JSON.stringify(formattedUserChoice)
        })

        message.error('识别失败，请重试')
        setRecognitionStatus(XC_RECOGNITION_STATUS.WAITING)
      }
    },
    [
      userId,
      groupId,
      buildingSafetyAIModelName,
      setRecognitionResult,
      setRecognitionStatus,
      dispatch,
      reportAISafetyHazardsResultClick
    ]
  )

  /**
   * 从示例图片添加
   */
  const addFromExample = useCallback(
    (url: string) => {
      // 检查用户和团队信息
      if (!userId || !groupId) {
        message.error('用户或团队信息缺失，请重新登录')
        return
      }

      // // 设置选中的图片
      // setSelectedImages([url])
      // message.success('示例图片添加成功')

      // // 自动调用识别
      // handleRecognition([url], userId)
    },
    [setSelectedImages, handleRecognition, userId, groupId]
  )

  /**
   * 选择照片 - 跳转页面选择图片并回调
   */
  const handleSelectPhotos = useCallback(() => {
    // 检查用户和团队信息
    if (!userId || !groupId) {
      message.error('用户或团队信息缺失，请重新登录')
      return
    }

    navigate(encodeURI(`/photo/picker?from=ai-recongnition&mediaType=0&max=1`))

    // // TODO: 实现跳转页面选择图片功能
    // // 暂时使用写死的图片链接
    // const imageUrl =
    //   'https://img2.baidu.com/it/u=1791803954,378182546&fm=253&app=138&f=JPEG?w=800&h=1067'

    // // 清空之前的图片，设置新图片
    // setSelectedImages([imageUrl])

    // // 自动调用识别
    // handleRecognition([imageUrl])
  }, [userId, groupId])

  return {
    selectedImages,
    addImage,
    addImages,
    addFromExample,
    handleSelectPhotos,
    handleRecognition
  }
}
